import React, { useRef, useCallback } from 'react';
import { CalendarProps } from '../types';
import { useCalendarState } from '../hooks/useCalendarState';
import { useCalendarData } from '../hooks/useCalendarData';
import { useHolidays } from '../hooks/useHolidays';
import { CalendarHeader } from '../shared/CalendarHeader';
import { WeekdayHeader } from '../shared/WeekdayHeader';
import { MobileDateCell } from './MobileDateCell';
import { MobileTodoList } from './MobileTodoList';
import { DragHandle } from './DragHandle';
import { useDragLogic } from './useDragLogic';
import { getCalendarDateInfo, isToday, isCurrentMonth, isSelected, toCalendarDateKey } from '../shared/utils';
import { IOS_EASING, ANIMATION_DURATION_MS } from '@ext-bookmark/shared/utils';

const CalendarMobile: React.FC<CalendarProps & { userId?: string | null }> = ({
  onTodoClick,
  onDateClick,
  onDateDoubleClick,
  selectedDate: externalSelectedDate,
  selectedGroupIds = new Set<string>(),
  personalGroupId,
  dataVersion,
  selectedDateTodos = [],
  onSelectedDateTodosChange,
  onTodosInvalidate,
  onTodoUpdated,
  getCurrentTodosForToggle,
  currentTodosForToggle,
  onOptimisticToggle,
  onBack,
  userId,
}) => {
  const {
    state,
    setCurrentDate,
    setMonthTodos,
    setHolidays,
    selectedDate,
  } = useCalendarState(externalSelectedDate, userId);

  const { loadMonthTodos: _loadMonthTodos } = useCalendarData(
    state.currentDate,
    selectedGroupIds,
    setMonthTodos,
    personalGroupId,
    dataVersion
  );

  useHolidays(state.currentDate, setHolidays);

  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const todoListRef = useRef<HTMLDivElement>(null);
  const calendarGridRef = useRef<HTMLDivElement>(null);

  const dateInfo = getCalendarDateInfo(state.currentDate, true);

  // 计算单行高度（像素）
  const getRowHeight = useCallback((): number => {
    if (!calendarContainerRef.current) return 0;
    const containerWidth = calendarContainerRef.current.offsetWidth;
    const gap = 6; // gap-1.5 = 6px
    const cellSize = (containerWidth - gap * 6) / 7;
    return cellSize + gap; // 单元格高度 + gap
  }, []);

  // 计算所有行的总高度（像素）
  const getAllRowsHeight = useCallback((): number => {
    return getRowHeight() * dateInfo.actualRows;
  }, [getRowHeight, dateInfo.actualRows]);

  // 计算选中日期所在的行（移动端）
  const getSelectedDateRow = useCallback((date: Date | null): number => {
    if (!date) return 0;
    const dateDay = date.getDate();
    return Math.floor((dateInfo.firstDayOfWeek + dateDay - 1) / 7);
  }, [dateInfo.firstDayOfWeek]);

  const {
    isDragging,
    getCalendarHeight,
    getCalendarTransform,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
  } = useDragLogic({
    isMobile: true,
    selectedDate,
    calendarContainerRef,
    calendarGridRef,
    getRowHeight,
    getAllRowsHeight,
    getSelectedDateRow,
    actualRows: dateInfo.actualRows,
    currentDate: state.currentDate,
  });

  // 切换到上个月
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(dateInfo.year, dateInfo.month - 1, 1));
  };

  // 切换到下个月
  const goToNextMonth = () => {
    setCurrentDate(new Date(dateInfo.year, dateInfo.month + 1, 1));
  };

  // 切换到今天
  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    handleDateClick(today);
  };

  // 选择日期（单击）
  const handleDateClick = (date: Date) => {
    onDateClick?.(date);
  };

  // 双击日期处理
  const handleDateDoubleClick = (date: Date) => {
    onDateDoubleClick?.(date);
  };

  return (
    <div className="glass-card flex flex-col overflow-hidden rounded-2xl shadow-lg ring-2 ring-border/80 border-2 border-border pb-0 rounded-t-none h-full min-h-0">
      <div className="flex flex-col flex-1 min-h-0 p-2">
        <CalendarHeader
          year={dateInfo.year}
          month={dateInfo.month}
          onPreviousMonth={goToPreviousMonth}
          onNextMonth={goToNextMonth}
          onToday={goToToday}
          isMobile={true}
          onBack={onBack}
        />

      <WeekdayHeader />

      {/* 日历网格 */}
      <div 
        ref={calendarContainerRef}
        className="flex-shrink-0 overflow-hidden relative"
        style={{
          height: getCalendarHeight(),
          transition: isDragging ? 'none' : `height ${ANIMATION_DURATION_MS}ms ${IOS_EASING}`,
          willChange: isDragging ? 'height' : 'auto',
        }}
      >
        <div 
          ref={calendarGridRef}
          className="grid grid-cols-7 gap-px p-0 rounded-b-lg overflow-hidden"
          style={{
            gridTemplateRows: `repeat(${dateInfo.actualRows}, minmax(0, 1fr))`,
            transform: getCalendarTransform(),
            transition: isDragging ? 'none' : `transform ${ANIMATION_DURATION_MS}ms ${IOS_EASING}`,
            willChange: isDragging ? 'transform' : 'auto',
            height: `${getAllRowsHeight()}px`,
          }}
        >
          {dateInfo.calendarDays.map((date, index) => {
            if (!date) {
              return <div key={index} />;
            }

            const dateKey = toCalendarDateKey(date);
            const dayTodos = state.monthTodos.get(dateKey) || [];
            const isCurrentMonthDay = isCurrentMonth(date, dateInfo.month);
            const isTodayDay = isToday(date);
            const isSelectedDay = isSelected(date, selectedDate);

            return (
              <MobileDateCell
                key={index}
                date={date}
                todos={dayTodos}
                isCurrentMonthDay={isCurrentMonthDay}
                isTodayDay={isTodayDay}
                isSelectedDay={isSelectedDay}
                holidays={state.holidays}
                currentUserId={state.currentUserId}
                onDateClick={handleDateClick}
              />
            );
          })}
        </div>
      </div>

      {/* 拖动指示条 */}
      <DragHandle
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
      />

      {/* 底部待办事项区域 */}
      <div 
        ref={todoListRef}
        className="flex-1 min-h-0 flex flex-col bg-white dark:bg-gray-800"
        style={{
          transition: isDragging ? 'none' : `flex-basis ${ANIMATION_DURATION_MS}ms ${IOS_EASING}`,
        }}
      >
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
          <MobileTodoList
            selectedDate={selectedDate}
            todos={selectedDateTodos}
            onTodoClick={onTodoClick || (() => {})}
            onDateDoubleClick={handleDateDoubleClick}
            onDateClick={handleDateClick}
            onTodosChange={onSelectedDateTodosChange || (() => {})}
            onTodosInvalidate={onTodosInvalidate}
            onTodoUpdated={onTodoUpdated}
            onOptimisticToggle={onOptimisticToggle}
            getCurrentTodosForToggle={getCurrentTodosForToggle}
            currentTodosForToggle={currentTodosForToggle}
          />
        </div>
      </div>
      </div>
    </div>
  );
};

export default CalendarMobile;
